import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLogto } from '@logto/react';
import config from '@/config';
import {
  API_RESOURCE,
  KNOWN_ROLES,
  PROJECT_ID,
  REDIRECT_URI,
  POST_SIGN_OUT_REDIRECT_URI,
} from '@/logto';

// Auth is provided by Logto (OIDC) at auth.parallelia.com. Sign-in happens on
// Logto's hosted page; this app only ever handles tokens.
//
// The shape exported by useAuth() is deliberately unchanged from the previous
// custom auth service, so the ~24 components that consume it did not have to
// change. What moved is where the data comes from:
//   - tokens        Logto SDK (it refreshes them itself)
//   - roles         `roles` claim on the ID token (UserScope.Roles)
//   - project       constant: this deployment serves only Luxottica

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Decode a JWT payload without verifying it. Only used as a fallback to read
// role names out of the access token's `scope`; the backend does the real
// verification against Logto's JWKS.
// Logto reports roles as plain strings in claims but as objects from userinfo.
const normaliseRoles = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((role) => (typeof role === 'string' ? role : role?.name))
    .filter(Boolean);
};

const decodeJwtPayload = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    ));
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    signIn,
    signOut,
    getAccessToken,
    getIdTokenClaims,
    // Renamed: this context exports its own fetchUserInfo below.
    fetchUserInfo: fetchLogtoUserInfo,
  } = useLogto();

  const [user, setUser] = useState(null);

  // Set when the profile lookup fails, so `loading` can't latch on forever.
  const [profileFailed, setProfileFailed] = useState(false);

  // The SDK's isLoading is NOT "session initialising" - it is a counter that
  // every SDK method (getAccessToken included) increments around each call. If
  // it were surfaced as `loading` directly, every API request would flip
  // loading true for a moment, ProtectedRoute would swap the page for its
  // spinner (unmounting it mid-fetch), and the remounted page's mount effects
  // would fetch again - an infinite mount/fetch loop that floods the browser
  // with requests. Latch the first completed init instead: after that, SDK
  // call activity never puts the app back into a loading state.
  const [sdkInitialised, setSdkInitialised] = useState(false);
  useEffect(() => {
    if (!isLoading) {
      setSdkInitialised(true);
    }
  }, [isLoading]);

  // Pull identity + roles out of the ID token once we're signed in.
  const loadProfile = useCallback(async () => {
    if (!isAuthenticated) {
      setUser(null);
      setProfileFailed(false);
      return;
    }

    setProfileFailed(false);
    try {
      const claims = await getIdTokenClaims();

      // Roles can arrive by several routes depending on tenant configuration,
      // so try each rather than depending on one:
      //   1. `roles` on the ID token (UserScope.Roles)
      //   2. the access token's `roles` claim (custom JWT template) or its
      //      `scope`, which under stock RBAC lists the granted role permissions
      //   3. the userinfo endpoint, which always reports them
      let roles = normaliseRoles(claims?.roles);

      if (roles.length === 0) {
        const accessToken = await getAccessToken(API_RESOURCE).catch(() => null);
        const payload = accessToken ? decodeJwtPayload(accessToken) : null;
        roles = normaliseRoles(payload?.roles);
        if (roles.length === 0) {
          roles = (payload?.scope || '')
            .split(' ')
            .filter((s) => KNOWN_ROLES.includes(s));
        }
      }

      let info = null;
      if (roles.length === 0) {
        info = await fetchLogtoUserInfo().catch(() => null);
        roles = normaliseRoles(info?.roles);
      }

      if (roles.length === 0) {
        console.warn(
          'Signed in but no roles found on the ID token, access token or userinfo. ' +
            'Check that the user has a role in Logto and that the `roles` scope is granted.'
        );
      }

      // The Logto subject is not a UUID, and the backend's tables are keyed by
      // Supabase users.id. Ask the API for the canonical identity so `user.id`
      // is the value the rest of the schema expects - anything sent back to the
      // backend (uploads, analytics) would otherwise be rejected.
      let identity = null;
      try {
        const accessToken = await getAccessToken(API_RESOURCE);
        const response = await fetch(`${config.BACKEND_API_ADDRESS}/users/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          identity = await response.json();
        } else {
          console.warn('Could not resolve canonical identity:', response.status);
        }
      } catch (error) {
        console.warn('Could not resolve canonical identity:', error);
      }

      setUser({
        // Fall back to the subject only if the lookup failed; better a stale id
        // than blocking sign-in entirely.
        id: identity?.id || claims?.sub,
        logtoId: claims?.sub,
        email: identity?.email || claims?.email || info?.email,
        name: identity?.name || claims?.name || info?.name,
        username: claims?.username,
        roles: identity?.roles?.length ? identity.roles : roles,
        projects: [{ id: PROJECT_ID, slug: PROJECT_ID }],
      });
    } catch (error) {
      console.error('Failed to load profile from Logto:', error);
      setUser(null);
      setProfileFailed(true);
    }
  }, [isAuthenticated, getIdTokenClaims, getAccessToken, fetchLogtoUserInfo]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // --- actions ----------------------------------------------------------

  // Kept callable with the old (email, password) signature so stray call sites
  // don't break; credentials are ignored because Logto owns the login form.
  const login = useCallback(() => signIn(REDIRECT_URI), [signIn]);

  const register = useCallback(
    () => signIn({ redirectUri: REDIRECT_URI, firstScreen: 'register' }),
    [signIn]
  );

  const logout = useCallback(
    () => signOut(POST_SIGN_OUT_REDIRECT_URI),
    [signOut]
  );

  // --- authorization helpers -------------------------------------------

  const hasRole = useCallback(
    (role) => Boolean(user?.roles?.includes(role)),
    [user]
  );

  // Single-project deployment: access means "is this the Luxottica project".
  const hasProjectAccess = useCallback(
    (projectId) => !projectId || projectId === PROJECT_ID,
    []
  );

  const hasProjectRole = useCallback(
    (projectId, role) => hasProjectAccess(projectId) && hasRole(role),
    [hasProjectAccess, hasRole]
  );

  const getCurrentProjectId = useCallback(() => PROJECT_ID, []);

  // --- API access -------------------------------------------------------

  // The SDK refreshes the access token as needed, so there is no manual refresh
  // dance here any more. A 401 that survives a fresh token means the session is
  // genuinely gone.
  const authenticatedFetch = useCallback(
    async (url, options = {}) => {
      const accessToken = await getAccessToken(API_RESOURCE);

      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.status === 401) {
        console.error('Unauthorized after presenting a fresh token; signing out');
        await logout();
        throw new Error('Session expired');
      }

      return response;
    },
    [getAccessToken, logout]
  );

  // Retained for API compatibility. The SDK owns refresh, so this just
  // re-reads the current token.
  const refreshToken = useCallback(
    () => getAccessToken(API_RESOURCE),
    [getAccessToken]
  );

  const fetchUserInfo = useCallback(async () => {
    await loadProfile();
    return user;
  }, [loadProfile, user]);

  const value = {
    user,
    // Loading until the SDK's first init completes, and - once authenticated -
    // until the profile (and therefore the roles) has landed, so ProtectedRoute
    // never evaluates an authenticated user with an empty role list. Uses the
    // latched sdkInitialised, NOT the SDK's live isLoading: that flips on every
    // token call and would remount the page tree per request (see above).
    loading: !sdkInitialised || (isAuthenticated && !user && !profileFailed),
    isAuthenticated,
    userProjects: user?.projects || [],
    login,
    register,
    logout,
    refreshToken,
    fetchUserInfo,
    hasProjectAccess,
    hasRole,
    hasProjectRole,
    authenticatedFetch,
    getCurrentProjectId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
